import type { User } from '@whispers/sdk';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { BottomNavigation } from '../components/BottomNavigation.js';
import { Navigation } from '../components/Navigation.js';
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';
import { getMembershipUrl, type MembershipTier } from '../utils/membership.js';
import { StoryLayout } from './StoryLayout.js';

type MembershipLayoutProps = {
  tier: MembershipTier;
  canAccess: (user: User | undefined) => boolean;
};

export const MembershipLayout = ({ tier, canAccess }: MembershipLayoutProps) => {
  const { user, isAuthenticated, isLoading: authIsLoading } = useAuth();
  const { isPreviewMode, isLoading: previewIsLoading } = useStoryblokPreview();
  const location = useLocation();

  if (authIsLoading || previewIsLoading) {
    return <Spinner />;
  }
  if (!isAuthenticated && !isPreviewMode) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }
  if (!isPreviewMode && !canAccess(user)) {
    return <Navigate to={getMembershipUrl(tier, location.pathname)} replace />;
  }

  return (
    <div className="min-h-dvh bg-background">
      <Navigation />
      <main className="mx-auto grid max-w-prose gap-6 px-4 pb-24">
        <StoryLayout>{isPreviewMode ? <PreviewPlaceholder /> : <Outlet />}</StoryLayout>
      </main>
      {isAuthenticated && <BottomNavigation />}
    </div>
  );
};
